import Vue from 'vue'

import { ALLOWED_PRINT_TEMPLATE_IDS } from '@/modules/dispatcher/constants/orders'
import { SORT_FIELD_MAP } from '@/modules/dispatcher/constants/sorting'
import {
    normalizeOrder,
    findOrderInRoutes,
    isOrderMatchingListContext,
} from '@/modules/dispatcher/utils/normalizeOrder'

import { savePrintGroups } from '../utils/printGroupsStorage'

const trimOrdersToLimit = (orders, limit) => {
    const normalizedLimit = Number(limit)

    if (!normalizedLimit || normalizedLimit <= 0) return orders

    return orders.slice(0, normalizedLimit)
}

const getOrderTotalPrice = order =>
    order.payments?.reduce(
        (sum, payment) => sum + (Number(payment.final_price) || 0),
        0
    ) || 0

const getOrderSortValue = (order, sortBy) => {
    if (sortBy === SORT_FIELD_MAP.amount) return getOrderTotalPrice(order)
    if (sortBy === SORT_FIELD_MAP.deliverTo)
        return new Date(order.delivered_date).getTime()
    return order[sortBy]
}

const compareOrderValues = (a, b) => {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1

    if (typeof a === 'number' && typeof b === 'number') {
        if (Number.isNaN(a) && Number.isNaN(b)) return 0
        if (Number.isNaN(a)) return 1
        if (Number.isNaN(b)) return -1

        return a - b
    }

    return String(a).localeCompare(String(b), 'ru-RU', { numeric: true })
}

const sortOrdersByContext = (orders, context) => {
    if (!context.sortBy) return orders

    return [...orders].sort((a, b) => {
        const result = compareOrderValues(
            getOrderSortValue(a, context.sortBy),
            getOrderSortValue(b, context.sortBy)
        )

        return context.sortDesc ? -result : result
    })
}

const normalizeOrdersPage = (orders, context) =>
    trimOrdersToLimit(sortOrdersByContext(orders, context), context.limit)

export default {
    // Заказы
    SET_ORDERS(state, orders) {
        state.orders = orders
        state.ordersUpdateSource = 'rest'
    },

    SET_CURRENT_ORDER(state, order) {
        state.currentOrder = order
    },

    RESET_CURRENT_ORDER(state) {
        state.currentOrder = null
    },

    SET_TOTAL_COUNT(state, totalCount) {
        state.totalCount = totalCount
    },

    SET_COURIERS(state, couriers) {
        state.couriers = couriers
    },

    SET_STATUSES_COUNT(state, statusesCount) {
        state.statusesCount = statusesCount
    },

    SET_ORDER_LIST_CONTEXT(state, context) {
        state.orderListContext = {
            ...state.orderListContext,
            ...context,
            filters: context.filters
                ? { ...context.filters }
                : state.orderListContext.filters,
        }
    },

    BUMP_CDC_ORDER_VERSION(state) {
        state.cdcOrderVersion += 1
    },

    CLEAR_ORDERS_UPDATE_SOURCE(state) {
        state.ordersUpdateSource = null
    },

    BUMP_CDC_ROUTE_VERSION(state) {
        state.cdcRouteVersion += 1
    },

    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading
    },

    // История изменений
    SET_ORDER_CHANGES_LOADING(state, v) {
        state.orderChangesLoading = v
    },

    SET_ORDER_CHANGES(state, changes) {
        state.orderChanges = changes
    },

    SET_ORDER_CHANGES_TOTAL(state, total) {
        state.orderChangesTotal = total
    },

    SET_PRINT_GROUPS(state, groups) {
        state.printGroups = groups
        savePrintGroups(groups)
    },

    ADD_PRINT_GROUP(state, { groupId, type, queueId, orders }) {
        Vue.set(state.printGroups, groupId, {
            type,
            queueId,
            orders,
            createdAt: Date.now(),
        })
        savePrintGroups(state.printGroups)
    },

    UPDATE_PRINT_ORDER_STATUS(
        state,
        { groupId, queueId, jobId, type, status, errorText }
    ) {
        const group = state.printGroups[groupId]
        if (!group) return

        if (!queueId) {
            console.warn(
                '[Mutation] queueId отсутствует, статус может обновиться некорректно'
            )
        }

        const job =
            type === ALLOWED_PRINT_TEMPLATE_IDS.FISCAL
                ? group.orders[0]
                : group.orders.find(o => o.jobId === jobId)

        if (job) {
            const wasCompleted =
                job.printStatus === 'success' || job.printStatus === 'error'
            Vue.set(job, 'printStatus', status)
            Vue.set(job, 'errorText', errorText || null)

            if (group.serverProgress && !wasCompleted) {
                const progress = { ...group.serverProgress }
                if (status === 'success') progress.completed += 1
                if (status === 'error') progress.failed += 1
                Vue.set(group, 'serverProgress', progress)
            }
        }

        savePrintGroups(state.printGroups)
    },

    SET_PRINT_GROUP_PROGRESS(state, { groupId, progress }) {
        const group = state.printGroups[groupId]
        if (!group) return

        Vue.set(group, 'serverProgress', progress)
        savePrintGroups(state.printGroups)
    },

    REMOVE_PRINT_GROUP(state, groupId) {
        Vue.delete(state.printGroups, groupId)
        savePrintGroups(state.printGroups)
    },

    // Маршруты
    SET_ROUTES(state, routes) {
        state.routes = routes
    },
    SET_ROUTES_COUNT(state, routesCount) {
        state.routesCount = routesCount
    },
    ADD_ROUTE(state, route) {
        state.routes.push(route)
    },
    REMOVE_ROUTE(state, routeId) {
        state.routes = state.routes.filter(route => route.id !== routeId)
    },
    SET_ROUTES_LOADING(state, isLoading) {
        state.routesLoading = isLoading
    },
    SET_ROUTE_CREATING(state, v) {
        state.routeCreating = v
    },
    RESET_ROUTES(state) {
        state.routes = []
        state.routesLoading = false
    },
    SET_ROUTE_STATUSES_COUNT(state, statuses) {
        state.routeStatusesCount = statuses
    },
    SET_ROUTE_FILTERS(state, filters) {
        state.routeFilters = { ...state.routeFilters, ...filters }
    },
    SET_ROUTE_STATUSES_LOADING(state, v) {
        state.routeStatusesLoading = v
    },
    UPDATE_ROUTE(state, route) {
        if (!route?.id) return
        const index = state.routes.findIndex(r => r.id === route.id)
        // Маршрут вне текущей выборки (фильтр дат/дивизион) — игнорируем,
        // чтобы CDC-событие не подмешивало чужую карточку в список.
        if (index === -1) return
        const prev = state.routes[index]
        Vue.set(state.routes, index, {
            ...prev,
            ...route,
            orders: route.orders ?? prev.orders,
        })
    },
    UPDATE_ORDER_IN_ROUTE(state, { order }) {
        if (!order?.id) return

        const normalizedOrder = normalizeOrder(order)

        const found = findOrderInRoutes(state.routes, normalizedOrder.id)
        if (!found) return

        const prevOrder = found.route.orders[found.orderIndex]

        // обрезаем "Т-" из номера заказа, приводим объект к формату заказа в маршруте
        const orderNumber = Number(normalizedOrder.num.match(/\d+/)?.[0] ?? '')
        const patch = {
            id: normalizedOrder.id,
            order_number: orderNumber || prevOrder.order_number,
            status: normalizedOrder.status.id ?? prevOrder.status,
            address: normalizedOrder.address?.raw_address ?? prevOrder.address,
            delivery_time:
                normalizedOrder.delivered_date ?? prevOrder.delivery_time,
        }

        Vue.set(found.route.orders, found.orderIndex, {
            ...prevOrder,
            ...patch,
        })
    },

    /**
     * Частичное обновление заказа в списке (для таблицы)
     * Обновляет только payments, isFavorite, isInvoicePrinted
     */
    UPDATE_ORDER_FAVORITE(state, { orderId, isFavorite }) {
        const orderIndex = state.orders.findIndex(o => o.id === orderId)
        if (orderIndex === -1) return

        const oldOrder = state.orders[orderIndex]

        // ✅ Создаём новый объект с обновлёнными полями
        const newOrder = {
            ...oldOrder,
            is_favorite: isFavorite ?? oldOrder.is_favorite,
        }

        // ✅ Заменяем весь объект в массиве — это гарантированно реактивно
        Vue.set(state.orders, orderIndex, newOrder)
    },

    /**
     * Полное обновление текущего заказа (для карточки)
     */
    UPDATE_CURRENT_ORDER(state, order) {
        if (state.currentOrder?.id === order.id) {
            state.currentOrder = order
        }
    },

    CLEAR_ORDER_LIVE_ADDED(state, orderId) {
        const orderIndex = state.orders.findIndex(o => o.id === orderId)
        if (orderIndex === -1) return

        state.ordersUpdateSource = 'local'
        Vue.set(state.orders, orderIndex, {
            ...state.orders[orderIndex],
            isLiveAdded: false,
        })
    },

    ADD_OR_UPDATE_ORDER(state, { order, prepend = false }) {
        if (!order) return

        const normalizedOrder = normalizeOrder(order)
        const context = state.orderListContext
        const existingIndex = state.orders.findIndex(
            o => o.id === normalizedOrder.id
        )
        const isMatchingList = isOrderMatchingListContext(
            normalizedOrder,
            context
        )

        if (existingIndex !== -1) {
            if (isMatchingList) {
                Vue.set(state.orders, existingIndex, {
                    ...normalizedOrder,
                    isLiveAdded: state.orders[existingIndex].isLiveAdded,
                })
                state.orders = normalizeOrdersPage(state.orders, context)
                state.ordersUpdateSource = 'cdc'
            } else {
                state.orders.splice(existingIndex, 1)
                state.ordersUpdateSource = 'cdc'
            }
            return
        }

        if (!isMatchingList) {
            return
        }

        const liveOrder = {
            ...normalizedOrder,
            isLiveAdded: true,
        }

        if (prepend) {
            state.orders.unshift(liveOrder)
        } else {
            state.orders.push(liveOrder)
        }

        state.orders = normalizeOrdersPage(state.orders, context)
        state.ordersUpdateSource = 'cdc'
    },
}
